import React, { createContext, useContext } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { CARD_WIDTH } from '../components/consts';
import { NARROW_WINDOW_WIDTH } from '../utils/useIsNarrowWindow';
/**
 * Phone-friendly layout for the public web sign-up form.
 *
 * ProfileView and its cards are shared with the admin "Add client" and
 * profile screens, which must render exactly as before. Only SignupScreen
 * wraps the form in <ResponsiveFormProvider>, so everything here is a no-op
 * elsewhere. Within sign-up, layout changes also only kick in below
 * NARROW_FORM_WIDTH, so desktop sign-up looks the same as before too.
 */
export const NARROW_FORM_WIDTH = NARROW_WINDOW_WIDTH;
/** Side margin of each card on a narrow window. */
export const NARROW_CARD_MARGIN = 12;
/**
 * Horizontal space a field loses inside a narrow card: card margins
 * (2 × 12), Card.Content padding (2 × 16) and CustomInput margin (2 × 10),
 * rounded up.
 */
const NARROW_FIELD_INSET = 80;
/** Inputs below 16px make iOS Safari zoom in on focus. */
export const NARROW_INPUT_FONT_SIZE = 16;
/** Minimum tap target, matching SignupConsentCheckbox. */
export const MIN_TAP_TARGET = 44;
const ResponsiveFormContext = createContext(false);
export const ResponsiveFormProvider = ({ children, }) => (<ResponsiveFormContext.Provider value={true}>
    {children}
  </ResponsiveFormContext.Provider>);
/**
 * ResponsiveFormProvider only when `enabled`: phone admins viewing a client's
 * Profile (#411) get the phone form; desktop admin renders as before.
 */
export const OptionalResponsiveFormProvider = ({ enabled, children }) => enabled ? (<ResponsiveFormProvider>{children}</ResponsiveFormProvider>) : (<>{children}</>);
/** True inside the public sign-up form (any window size). */
export const useIsResponsiveForm = () => useContext(ResponsiveFormContext);
/** True inside the public sign-up form on a narrow (phone-sized) window. */
export const useIsNarrowForm = () => {
    const enabled = useContext(ResponsiveFormContext);
    const { width } = useWindowDimensions();
    return enabled && width < NARROW_FORM_WIDTH;
};
/** Pure helpers, exported for tests. */
export const narrowFieldWidth = (width, windowWidth) => Math.max(0, Math.min(width, windowWidth - NARROW_FIELD_INSET));
export const narrowCardStyle = (windowWidth) => ({
    width: Math.min(CARD_WIDTH, windowWidth - 2 * NARROW_CARD_MARGIN),
    marginLeft: NARROW_CARD_MARGIN,
    marginRight: NARROW_CARD_MARGIN,
});
/**
 * Returns a function that caps a field width to the window on narrow
 * sign-up, and returns the width unchanged everywhere else.
 */
export const useFieldWidth = () => {
    const narrow = useIsNarrowForm();
    const { width: windowWidth } = useWindowDimensions();
    return (width) => narrow ? narrowFieldWidth(width, windowWidth) : width;
};
/** Extra card style on narrow sign-up; undefined everywhere else. */
export const useNarrowCardStyle = () => {
    const narrow = useIsNarrowForm();
    const { width: windowWidth } = useWindowDimensions();
    return narrow ? narrowCardStyle(windowWidth) : undefined;
};
/** 16px input font on narrow web sign-up so iOS Safari doesn't zoom. */
export const useNarrowInputFontSize = () => {
    const narrow = useIsNarrowForm();
    return narrow && Platform.OS === 'web' ? NARROW_INPUT_FONT_SIZE : undefined;
};
